/**
 * Iter 67ch — Admin: Blog posts (Destinations + Travel Tips).
 *
 * One CMS covers both blog modules — switch the category dropdown to bucket
 * posts. Markdown editor with side-by-side preview, image upload via the
 * existing /api/upload endpoint, plus the full SEO field set (slug, meta
 * title/description, OG image, featured flag).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, Plus, Pencil, Trash2, Loader2, Save, Upload, Globe2, Star,
  Eye, EyeOff, Image as ImageIcon, ExternalLink,
} from "lucide-react";

const CATEGORIES = [
  { value: "destinations", label: "Destinations" },
  { value: "travel-tips",  label: "Travel tips" },
];

const _newDraft = (category = "destinations") => ({
  category,
  title: "",
  slug: "",
  country: "",
  excerpt: "",
  body_md: "",
  featured_image: "",
  meta_title: "",
  meta_description: "",
  og_image: "",
  is_published: false,
  is_featured: false,
});

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

// Lightweight Markdown → HTML for the editor preview. Intentionally minimal
// (no third-party dep needed). Covers headings, bold, italic, links, lists.
function _renderMarkdownPreview(src) {
  if (!src) return "";
  let html = src
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^###### (.*)$/gm, "<h6>$1</h6>")
    .replace(/^##### (.*)$/gm, "<h5>$1</h5>")
    .replace(/^#### (.*)$/gm, "<h4>$1</h4>")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/gs, "<ul>$1</ul>");
  // Paragraphs from blank-line splits.
  html = html.split(/\n{2,}/).map((p) => /^<(h\d|ul|ol|p)/.test(p) ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("\n");
  return html;
}

export default function AdminBlogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("destinations");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(_newDraft());
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const featuredFileRef = useRef(null);
  const ogFileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/admin/blog")).data || []); }
    catch (err) { toast.error(formatApiError(err) || "Could not load posts."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => r.category === categoryFilter), [rows, categoryFilter]);

  const onCreate = () => {
    setEditing(null);
    setDraft(_newDraft(categoryFilter));
    setDialogOpen(true);
  };
  const onEdit = async (r) => {
    // Fetch full payload (list omits body_md).
    try {
      const { data } = await api.get(`/admin/blog/${r.id}`);
      setEditing(data);
      setDraft({
        category: data.category,
        title: data.title || "",
        slug: data.slug || "",
        country: data.country || "",
        excerpt: data.excerpt || "",
        body_md: data.body_md || "",
        featured_image: data.featured_image || "",
        meta_title: data.meta_title || "",
        meta_description: data.meta_description || "",
        og_image: data.og_image || "",
        is_published: !!data.is_published,
        is_featured: !!data.is_featured,
      });
      setDialogOpen(true);
    } catch (err) { toast.error(formatApiError(err) || "Could not open post."); }
  };

  const onSave = async () => {
    if (!draft.title.trim()) { toast.error("Title required."); return; }
    if (!draft.body_md.trim()) { toast.error("Body required."); return; }
    setSaving(true);
    try {
      const body = {
        category: draft.category,
        title: draft.title.trim(),
        slug: draft.slug.trim() || undefined,
        country: draft.country.trim() || null,
        excerpt: draft.excerpt.trim() || null,
        body_md: draft.body_md,
        featured_image: draft.featured_image.trim() || null,
        meta_title: draft.meta_title.trim() || null,
        meta_description: draft.meta_description.trim() || null,
        og_image: draft.og_image.trim() || null,
        is_published: !!draft.is_published,
        is_featured: !!draft.is_featured,
      };
      if (editing) await api.patch(`/admin/blog/${editing.id}`, body);
      else await api.post("/admin/blog", body);
      toast.success(editing ? "Saved." : "Published.");
      setDialogOpen(false); load();
    } catch (err) { toast.error(formatApiError(err) || "Save failed."); }
    finally { setSaving(false); }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await api.delete(`/admin/blog/${r.id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
  };

  const onTogglePublished = async (r) => {
    try { await api.patch(`/admin/blog/${r.id}`, { is_published: !r.is_published }); load(); }
    catch (err) { toast.error(formatApiError(err) || "Toggle failed."); }
  };

  const onFileChange = async (e, field) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingField(field);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, [field]: data.path }));
      toast.success("Uploaded.");
    } catch (err) { toast.error(formatApiError(err) || "Upload failed."); }
    finally {
      setUploadingField(null);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-blog-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-medium flex items-center gap-2"><BookOpen className="h-5 w-5 text-indigo-600" /> Blog</h3>
          <p className="text-xs text-neutral-500 max-w-2xl">Destinations &amp; travel tips for the homepage and SEO landing pages. Each post supports full SEO fields and a country tag.</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={categoryFilter} onValueChange={setCategoryFilter}>
            <TabsList className="bg-neutral-100">
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c.value} value={c.value} data-testid={`admin-blog-tab-${c.value}`}>{c.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button onClick={onCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="blog-add">
            <Plus className="h-4 w-4 mr-1.5" /> New post
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        {loading && <div className="py-10 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-neutral-500">No posts in {CATEGORIES.find((c) => c.value === categoryFilter)?.label} yet.</div>
        )}
        {!loading && filtered.map((r) => (
          <div key={r.id} className="px-5 py-4 border-b border-border last:border-0 flex items-center gap-4" data-testid={`blog-row-${r.id}`}>
            <div className="h-14 w-20 rounded-lg bg-neutral-100 overflow-hidden flex-shrink-0">
              {r.featured_image
                ? <img src={_resolveSrc(r.featured_image)} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-5 w-5 text-neutral-400" /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm truncate">{r.title}</span>
                {r.is_featured && <Badge className="bg-amber-100 text-amber-800 text-[10px]"><Star className="h-3 w-3 mr-0.5" />Featured</Badge>}
                {r.is_published
                  ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]"><Eye className="h-3 w-3 mr-0.5" />Published</Badge>
                  : <Badge variant="secondary" className="text-[10px]"><EyeOff className="h-3 w-3 mr-0.5" />Draft</Badge>}
                {r.country && <Badge variant="outline" className="text-[10px]"><Globe2 className="h-3 w-3 mr-0.5" />{r.country}</Badge>}
              </div>
              <div className="text-[11px] text-neutral-500 mt-0.5 truncate">/{r.slug}</div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {r.is_published && (
                <Button asChild variant="ghost" size="icon" className="h-8 w-8" title="View public post">
                  <a href={`/blog/${r.category}/${r.slug}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                </Button>
              )}
              <Switch checked={!!r.is_published} onCheckedChange={() => onTogglePublished(r)} className="data-[state=checked]:bg-emerald-600" data-testid={`blog-publish-${r.id}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`blog-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(r)} data-testid={`blog-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit post" : "New post"}</DialogTitle>
            <DialogDescription>Markdown body with side-by-side preview. All SEO fields are optional but recommended.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="blog-dialog-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country (optional)</Label>
                <Input value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value })} placeholder="e.g. Italy" className="mt-1" data-testid="blog-dialog-country" />
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1" data-testid="blog-dialog-title" />
            </div>
            <div>
              <Label>Slug <span className="text-xs text-neutral-400">(auto-generated if blank)</span></Label>
              <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="albanian-riviera-road-trip" className="mt-1 font-mono text-xs" data-testid="blog-dialog-slug" />
            </div>
            <div>
              <Label>Excerpt</Label>
              <Textarea value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} rows={2} className="mt-1" data-testid="blog-dialog-excerpt" />
            </div>

            <div>
              <Label>Featured image</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input value={draft.featured_image} onChange={(e) => setDraft({ ...draft, featured_image: e.target.value })} placeholder="Paste URL or upload" className="flex-1 text-xs" data-testid="blog-dialog-featured-image" />
                <Button variant="outline" type="button" onClick={() => featuredFileRef.current?.click()} disabled={uploadingField === "featured_image"}>
                  {uploadingField === "featured_image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                </Button>
                <input ref={featuredFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFileChange(e, "featured_image")} />
              </div>
              {draft.featured_image && <div className="mt-2 rounded-lg overflow-hidden border border-border aspect-video bg-neutral-50"><img src={_resolveSrc(draft.featured_image)} alt="" className="w-full h-full object-cover" /></div>}
            </div>

            <div>
              <Label>Body (Markdown)</Label>
              <div className="mt-1 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Textarea
                  value={draft.body_md}
                  onChange={(e) => setDraft({ ...draft, body_md: e.target.value })}
                  rows={12}
                  placeholder="# H1 heading&#10;&#10;Markdown body — supports ## H2, **bold**, *italic*, [links](https://), and - bullet lists."
                  className="font-mono text-xs"
                  data-testid="blog-dialog-body"
                />
                <div
                  className="prose prose-sm max-w-none rounded-lg border border-border bg-neutral-50/40 p-3 overflow-y-auto text-sm"
                  style={{ minHeight: "16rem" }}
                  dangerouslySetInnerHTML={{ __html: _renderMarkdownPreview(draft.body_md) || "<em style='color:#999'>Preview…</em>" }}
                />
              </div>
            </div>

            <details className="rounded-lg border border-border bg-neutral-50/40 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-neutral-600">SEO fields</summary>
              <div className="grid grid-cols-1 gap-3 mt-3">
                <div>
                  <Label>Meta title <span className="text-xs text-neutral-400">({(draft.meta_title || "").length}/60)</span></Label>
                  <Input value={draft.meta_title} onChange={(e) => setDraft({ ...draft, meta_title: e.target.value })} className="mt-1" data-testid="blog-dialog-meta-title" />
                </div>
                <div>
                  <Label>Meta description <span className="text-xs text-neutral-400">({(draft.meta_description || "").length}/160)</span></Label>
                  <Textarea rows={2} value={draft.meta_description} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} className="mt-1" data-testid="blog-dialog-meta-description" />
                </div>
                <div>
                  <Label>OG image (social share)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input value={draft.og_image} onChange={(e) => setDraft({ ...draft, og_image: e.target.value })} placeholder="Paste URL or upload" className="flex-1 text-xs" data-testid="blog-dialog-og-image" />
                    <Button variant="outline" type="button" onClick={() => ogFileRef.current?.click()} disabled={uploadingField === "og_image"}>
                      {uploadingField === "og_image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </Button>
                    <input ref={ogFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFileChange(e, "og_image")} />
                  </div>
                </div>
              </div>
            </details>

            <div className="flex flex-wrap items-center gap-4 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!draft.is_published} onCheckedChange={(v) => setDraft({ ...draft, is_published: !!v })} className="data-[state=checked]:bg-emerald-600" data-testid="blog-dialog-published" />
                Published
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!draft.is_featured} onCheckedChange={(v) => setDraft({ ...draft, is_featured: !!v })} className="data-[state=checked]:bg-amber-500" data-testid="blog-dialog-featured" />
                Featured
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="blog-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
